import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  ClosestAssetRefRelatedDocumentsRequest,
  DocumentSelectionInfo,
  MassNotification,
  MassRecipientType,
  ToRecipientSettings
} from 'src/app/modules/communications/store/communications/communications.model';
import { DataTableServerComponent } from 'src/app/modules/core/components/data-table-server/data-table.component';
import { GetRowIdParams, IServerSideDatasource, ValueFormatterParams } from 'ag-grid-enterprise';
import { GridHelperService } from 'src/app/modules/core/services/grid-helper.service';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import {
  DocumentQueryParams,
  FieldDataType,
  FilterGroup,
  FilterGroupType,
  RuleOperator,
  SystemFieldNames
} from 'src/app/modules/core/store/models';
import { UserContextService } from 'src/app/modules/core/services/user-context.service';
import { ClosestAssetCountByLayer, ClosestAssetFilterDto } from 'src/app/modules/cop/store/impacted-assets';
import { TableColumnsService } from 'src/app/modules/cop/services/table/table-columns-service';
import { ImpactedAssetsApiService } from 'src/app/modules/cop/services/impacted-assets-api.service';
import { Layer } from 'src/app/modules/cop/store/cop/models/layer.model';
import { DataApiService } from 'src/app/modules/management/store/data/data-api.service';
import { DatasetApiService } from 'src/app/modules/core/services/dataset-api.service';
import { Subject } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import * as fromComm from 'src/app/modules/communications/store/communications';
import * as fromCop from 'src/app/modules/cop/store/cop';
import * as fromUserProfile from 'src/app/modules/core/store/user-profile';
import { CommunicationsApiService } from '@app/modules/communications/store/communications/communications-api.service';
import { SelectedRecipientInfo } from '@app/modules/core/communications/recipients/recipient-list-select/recipient-list-select.component';
import { Store } from '@ngrx/store';
import { take, takeUntil } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { TemplateDesignerService } from '../../services/template-designer.service';
import { RelatedViewsService } from '@app/modules/core/components/related-documents/services/related-views.service';
import {
  AssetsElementItem,
  AssetsElementSettings
} from '../template/designer/design-elements/controls/assets-control/assets-control-model';
import {
  RecipientAssetsSelectionRequest,
  RecipientAssetsSelectionRequests,
  SelectedAssetInfo
} from '../recipient-assets-selection-list/recipient-assets-selection-list.component';
import { GUID_EMPTY } from '@app/core/constants';

@Component({
    selector: 'topo-comms-recipient-assets-selection',
    templateUrl: './recipient-assets-selection.component.html',
    styleUrls: ['./recipient-assets-selection.component.scss'],
    standalone: false
})
export class RecipientAssetsSelectionComponent implements OnInit, AfterViewInit, OnDestroy {
  _notification: MassNotification;
  @Input() set notification(value: MassNotification) {
    this._notification = value;
    if (this._notification.uId !== GUID_EMPTY) {
      this.includeAssetsRecipients = !!this._notification?.toClosestAssetsSelectionFilter;
    }
  }

  _closestAssetRequest: ClosestAssetFilterDto;
  @Input() set closestAssetRequest(value: ClosestAssetFilterDto) {
    this._closestAssetRequest = value;
    this._document = null;
    this.assetsSelectionContext = null;
    this.assetRecipientCountsByLayer = [];
    this.requestClosesetAssets();
  }

  _document: Document;
  @Input() set document(value: Document) {
    this._document = value;
  }

  @Output() assetsElementSettingsChanged = new EventEmitter<AssetsElementSettings>();
  @Output() selectedRecipientsChanged = new EventEmitter<{
    includeAssetsRecipients: boolean;
    assetRecipientCountsByLayer: ClosestAssetCountByLayerEx[];
  }>();

  @Output() totalAssetsCountChanged = new EventEmitter<number>();

  assetsSelectionContext: RecipientAssetsSelectionRequests;
  includeAssetsRecipients: boolean;
  autoSelectAssetsRecipients: boolean;
  assetRecipientCountsByLayer: ClosestAssetCountByLayerEx[];
  totalAssetRecipients: number;

  destroy$: Subject<void> = new Subject<void>();

  constructor(
    private store: Store<fromComm.State>,
    private impactedAssetsApiService: ImpactedAssetsApiService,
    private relatedViewsService: RelatedViewsService,
    private changeDetector: ChangeDetectorRef,
    private commApiService: CommunicationsApiService,
    private templateDesignerService: TemplateDesignerService
  ) {
    this.store
      .select(fromUserProfile.selectOrganization)
      .pipe(takeUntil(this.destroy$))
      .subscribe(org => {
        this.autoSelectAssetsRecipients = org.mapSettings?.autoSelectAssetsRecipients;
      });
  }

  ngOnInit(): void {}

  ngAfterViewInit() {}

  refreshClosesetAssets(): void {
    if (!!this._document) {
      this.requestClosesetAssets();
    }
  }

  private postUpdate(): void {
    this.selectedRecipientsChanged.emit({
      includeAssetsRecipients: this.includeAssetsRecipients,
      assetRecipientCountsByLayer: this.assetRecipientCountsByLayer
    });
  }

  private requestClosesetAssets(): void {
    if (!!this._closestAssetRequest) {
      const closestAssetRequest = cloneDeep(this._closestAssetRequest);
      this.store
        .select(fromCop.selectSelectedMapState)
        .pipe(take(1))
        .subscribe(mapState => {
          this.impactedAssetsApiService.getClosestAssetCountsByLayer(closestAssetRequest).subscribe(res => {
            if (!!res) {
              this.assetRecipientCountsByLayer = [];
              res.forEach(result => {
                const relatedInfo = this.getRelatedAssetsInfo(mapState.layersSet[result.layerUId], mapState);
                const documentsInfo = this.getAssetDocumentsInfo(mapState.layersSet[result.layerUId]);
                const assetRecipientCounts = {
                  layer: mapState.layersSet[result.layerUId],
                  count: result.count,
                  relatedInfoItems: cloneDeep(relatedInfo),
                  include: !this.getIsAssetsExcluded(mapState.layersSet[result.layerUId]),
                  selectInfo: {
                    selectAll: documentsInfo?.selectAll !== false,
                    documentUIds: documentsInfo?.documentUIds ?? []
                  }
                };

                this.assetRecipientCountsByLayer.push(assetRecipientCounts);
                this.loadAssetRecipientRelatedCounts(assetRecipientCounts);
              });

              this.updateDesignClosestAssetsRuntimeData();
              this.totalAssetsCountChanged.emit(this.totalAssetRecipients);
            }

            this.includeAssetsRecipients = this.assetRecipientCountsByLayer?.length > 0;
            this.onIncludeAssetsRecipientsChanged(!this.autoSelectAssetsRecipients);
          });
        });
    }
  }

  private updateDesignClosestAssetsRuntimeData(): void {
    const assetItems = [] as AssetsElementItem[];
    this.assetRecipientCountsByLayer.forEach(a => {
      assetItems.push({
        name: a.layer?.name,
        count: a.count
      });
    });

    const data = {
      items: assetItems
    } as AssetsElementSettings;

    this.assetsElementSettingsChanged.emit(data);
  }

  private getRelatedAssetsInfo(layer: Layer, mapState: any): ClosestAssetRelatedInfo[] {
    const results = [];
    const dataset = this.templateDesignerService.datasets?.find(d => d.uId === layer?.datasetUId);
    dataset?.schema?.fields
      .filter(f => f.dataType === FieldDataType.ReferenceCriteria)
      ?.forEach(field => {
        if (!!field?.settings?.targetDatasetUId) {
          const documentsRelatedInfo = this.getAssetRelatedDocumentsInfo(mapState.layersSet[layer.uId], field.systemName);

          const targetDataset = this.templateDesignerService.datasets?.find(d => d.uId === field?.settings?.targetDatasetUId);
          results.push({
            datasetUId: targetDataset?.uId,
            datasetName: targetDataset?.name,
            fieldName: field.systemName!,
            include: !!documentsRelatedInfo,
            iconPath: targetDataset?.iconPath,
            isCustomIcon: targetDataset?.isCustomIcon,
            color: targetDataset?.defaultColor,
            hasDocumentFilter: this.relatedViewsService.linkFilterHasDocumentFilter(field.settings?.filterGroup),
            selectInfo: {
              selectAll: documentsRelatedInfo?.selectAll === true,
              documentUIds: documentsRelatedInfo?.documentUIds ?? []
            }
          });
        }
      });
    return results;
  }

  private getIsAssetsExcluded(layer: Layer): boolean {
    const asset = this._notification?.recipientSettings?.sendToClosestAssetsSettings?.find(s => s.datasetUId === layer?.datasetUId);
    return asset?.exclude === true;
  }

  private getAssetDocumentsInfo(layer: Layer): DocumentSelectionInfo {
    const asset = this._notification?.recipientSettings?.sendToClosestAssetsSettings?.find(s => s.datasetUId === layer?.datasetUId);
    return asset?.selectedDocumentsInfo;
  }

  private getAssetRelatedDocumentsInfo(layer: Layer, fieldName: string): DocumentSelectionInfo {
    const asset = this._notification?.recipientSettings?.sendToClosestAssetsSettings?.find(s => s.datasetUId === layer?.datasetUId);
    return asset?.sendToRefRelatedAssets?.find(r => r.fieldName == fieldName)?.selectedDocumentsInfo;
  }

  private getSelectInfoFilterGroup(selectInfo: DocumentSelectionInfo): FilterGroup {
    if (!!!selectInfo) {
      return null;
    }

    if (selectInfo?.documentUIds?.length > 0) {
      let filterGroup: FilterGroup = {
        filterGroups: [],
        filters: [],
        groupType: FilterGroupType.And,
        uId: uuidv4()
      };

      filterGroup.filters.push({
        field: SystemFieldNames.uId,
        operator: selectInfo?.selectAll === true ? RuleOperator.NotIn : RuleOperator.In,
        value: selectInfo?.documentUIds.join(',')
      });

      return filterGroup;
    } else {
      if (selectInfo?.selectAll === true) {
        return null;
      } else {
        let filterGroup: FilterGroup = {
          filterGroups: [],
          filters: [],
          groupType: FilterGroupType.And,
          uId: uuidv4()
        };

        filterGroup.filters.push({
          field: SystemFieldNames.uId,
          operator: RuleOperator.Equal,
          value: uuidv4()
        });

        return filterGroup;
      }
    }
  }

  private loadAssetRecipientRelatedCounts(closestAsset: ClosestAssetCountByLayerEx): void {
    const datasetUId = closestAsset.layer?.datasetUId;
    if (!!!datasetUId) {
      return;
    }

    const selectedCount = this.getSelectedCount(closestAsset);

    let queryMatchRule: DocumentQueryParams = null;

    const filterGroup = this.getSelectInfoFilterGroup(closestAsset.selectInfo);
    if (!!filterGroup) {
      queryMatchRule = {
        filterGroup: filterGroup
      } as DocumentQueryParams;
    }

    closestAsset.relatedInfoItems?.forEach(rel => {
      if (selectedCount === 0 && rel.hasDocumentFilter === true) {
        rel.count = 0;
        rel.countDisplay = rel.count.toString();
        rel.datasetUId = null;
        rel.limitedCounts = false;
        rel.contactCount = 0;
        this.calculateTotalRecipients();
        this.changeDetector.detectChanges();
      } else {
        const request = {
          refFieldname: rel.fieldName,
          queryMatchRule: queryMatchRule,
          locationGeoJson: this._closestAssetRequest?.locationGeoJson,
          distanceGeoJson: this._closestAssetRequest?.distanceGeoJson,
          withinMeters: this._closestAssetRequest?.withinMeters,
          totalCountOnly: true,
          countsOnly: true
        } as ClosestAssetRefRelatedDocumentsRequest;
        this.commApiService.getClosestAssetRefRelatedDocuments(datasetUId, request).subscribe(sres => {
          rel.count = sres.totalCount;
          rel.countDisplay = sres.limitedCounts === true ? `> ${rel.count}` : rel.count.toString();
          rel.datasetUId = sres.targetDatasetUId;
          rel.limitedCounts = sres.limitedCounts;
          const selectedCount = this.getRelatedSelectedCount(rel);
          rel.contactCount = selectedCount * this.templateDesignerService.getNumberOfContactFields(sres.targetDatasetUId);

          this.calculateTotalRecipients();
          this.changeDetector.detectChanges();
        });
      }
    });
  }

  getSelectedCount(asset: ClosestAssetCountByLayerEx): number {
    const selected = asset.selectInfo?.documentUIds?.length ?? 0;
    return asset.selectInfo?.selectAll === true ? asset.count - selected : selected;
  }

  getRelatedSelectedCount(rel: ClosestAssetRelatedInfo): number {
    if (rel.include === false) {
      return 0;
    }
    if (rel.hasDocumentFilter === true) {
      return rel.count;
    }
    const selected = rel.selectInfo?.documentUIds?.length ?? 0;
    return rel.selectInfo?.selectAll === true ? rel.count - selected : selected;
  }

  private calculateTotalRecipients(): void {
    this.totalAssetRecipients = 0;
    if (!!this.assetRecipientCountsByLayer && this.includeAssetsRecipients === true) {
      this.assetRecipientCountsByLayer.forEach(a => {
        a.contactCount = 0;
        a.contactCountDisplay = a.contactCount.toString();
        if (a.include == true) {
          let selectedCount = this.getSelectedCount(a);
          let count = selectedCount * this.templateDesignerService.getNumberOfContactFields(a.layer?.datasetUId);
          a.relatedInfoItems?.forEach(rel => {
            rel.contactCount = 0;
            if (rel.include === true) {
              const relatedCount = this.getRelatedSelectedCount(rel);
              selectedCount += relatedCount;
              rel.contactCount = relatedCount * this.templateDesignerService.getNumberOfContactFields(rel?.datasetUId);
              count += rel.contactCount;
            }
          });
          a.contactCount = count;
          a.contactCountDisplay = a.limitedCounts === true ? `> ${a.contactCount}` : a.contactCount.toString();
          this.totalAssetRecipients += selectedCount;
        }
      });
    }

    this.totalAssetsCountChanged.emit(this.totalAssetRecipients);

    this.changeDetector.markForCheck();
  }

  onSelectAllAssets(): void {
    this.assetsSelectionContext = {
      requests: [],
      canSelect: true,
      canSelectAll: true
    };

    this.assetRecipientCountsByLayer?.forEach(asset => {
      if (asset.include === true) {
        this.assetsSelectionContext.requests.push({
          uId: uuidv4(),
          layer: asset.layer,
          assetsSelectionFilter: this._closestAssetRequest
        } as RecipientAssetsSelectionRequest);
      }
    });
  }

  onSelectAsset(asset: ClosestAssetCountByLayerEx, event: any): void {
    this.assetsSelectionContext = {
      requests: [],
      canSelect: true,
      canSelectAll: true
    };

    this.assetsSelectionContext.requests.push({
      uId: uuidv4(),
      layer: asset.layer,
      assetsSelectionFilter: this._closestAssetRequest
    } as RecipientAssetsSelectionRequest);
  }

  onSelectAssetRelated(asset: ClosestAssetCountByLayerEx, rel: ClosestAssetRelatedInfo): void {
    const dataset = this.templateDesignerService.datasets?.find(d => d.uId === asset.layer?.datasetUId);
    const field = dataset?.schema?.fields.find(f => f.systemName === rel.fieldName);
    if (!!field) {
      this.assetsSelectionContext = {
        requests: [],
        canSelect: true,
        canSelectAll: true
      };
      this.assetsSelectionContext.requests.push({
        uId: uuidv4(),
        assetDatasetUId: dataset?.uId,
        parentDatasetName: dataset.name,
        targetDatasetUId: rel.datasetUId,
        targetFilterGroup: this.relatedViewsService.convertReferenceFilterToRegularFilter(field.settings?.filterGroup, null)
      } as RecipientAssetsSelectionRequest);
    }
  }

  onIncludeAssetsRecipientsChanged(clicked: boolean): void {
    if (clicked) {
      if (this.assetsInIndeterminateState() == true) {
        this.includeAssetsRecipients = false;
      } else {
        this.includeAssetsRecipients = !this.includeAssetsRecipients;
      }
      this.assetRecipientCountsByLayer.forEach(a => {
        a.include = this.includeAssetsRecipients;
        a.selectInfo = {
          selectAll: a.include,
          documentUIds: []
        };
      });
    }
    this.postUpdate();
    this.calculateTotalRecipients();
  }

  assetsInIndeterminateState(): boolean {
    return (
      this.assetRecipientCountsByLayer?.some(a => this.assetInIndeterminateState(a)) ||
      (this.assetRecipientCountsByLayer?.some(a => a.include == false) && this.assetRecipientCountsByLayer?.some(a => a.include == true))
    );
  }

  assetInIndeterminateState(lay: ClosestAssetCountByLayerEx): boolean {
    return lay.include && this.getSelectedCount(lay) !== lay.count;
  }

  onIncludeAssetRecipientChanged(lay: ClosestAssetCountByLayerEx, event: any): void {
    event?.stopPropagation();

    lay.include = !lay.include;
    lay.selectInfo = {
      selectAll: lay.include,
      documentUIds: []
    };

    if (lay.include === false) {
      lay.relatedInfoItems.forEach(a => {
        a.include = false;
        a.selectInfo = {
          selectAll: a.include,
          documentUIds: []
        };
      });
    }
    this.includeAssetsRecipients = this.assetRecipientCountsByLayer?.some(a => a.include == true);
    this.loadAssetRecipientRelatedCounts(lay);
    this.postUpdate();
    this.calculateTotalRecipients();
  }

  onSendToRelatedAssetsChanged(lay: ClosestAssetCountByLayerEx, rel: ClosestAssetRelatedInfo, event: any): void {
    event?.stopPropagation();

    if (!lay.include || !this.includeAssetsRecipients) {
      return;
    }

    rel.include = !rel.include;

    rel.selectInfo = {
      selectAll: rel.include,
      documentUIds: []
    };

    this.postUpdate();
    this.calculateTotalRecipients();
  }

  onCloseRecipientSelection(): void {
    this.assetsSelectionContext = null;
  }

  assetRecipientsSelected(data: SelectedAssetInfo): void {
    let assetRecipient = null;
    const selectionContext = this.assetsSelectionContext.requests.find(s => s.uId === data.uId);

    if (!!selectionContext?.targetDatasetUId) {
      assetRecipient = this.assetRecipientCountsByLayer.find(a => a.layer?.datasetUId == selectionContext?.assetDatasetUId);
      const relatedData = assetRecipient?.relatedInfoItems?.find(r => r.datasetUId == selectionContext?.targetDatasetUId);
      if (!!!relatedData.selectInfo) {
        relatedData.selectInfo = { selectAll: false, documentUIds: [] };
      }
      relatedData.selectInfo.selectAll = data.selectInfo.selectAll;
      relatedData.selectInfo.documentUIds = [];

      data.selectInfo.documentUIds.forEach(d => {
        relatedData.selectInfo.documentUIds.push(d);
      });

      relatedData.include = this.getRelatedSelectedCount(relatedData) > 0;
    } else {
      assetRecipient = this.assetRecipientCountsByLayer.find(a => a.layer?.datasetUId == selectionContext.layer.datasetUId);
      if (!!!assetRecipient.selectInfo) {
        assetRecipient.selectInfo = { selectAll: false, documentUIds: [] };
      }
      assetRecipient.selectInfo.selectAll = data.selectInfo.selectAll;
      assetRecipient.selectInfo.documentUIds = [];

      data.selectInfo.documentUIds.forEach(d => {
        assetRecipient.selectInfo.documentUIds.push(d);
      });

      assetRecipient.include = this.getSelectedCount(assetRecipient) > 0;

      this.loadAssetRecipientRelatedCounts(assetRecipient);
    }
    this.postUpdate();
    this.calculateTotalRecipients();
  }

  getAssetSelectedUIds = (uId: string): DocumentSelectionInfo => {
    const selectionContext = this.assetsSelectionContext.requests.find(s => s.uId === uId);
    if (!!selectionContext?.targetDatasetUId) {
      const assetRecipient = this.assetRecipientCountsByLayer.find(a => a.layer?.datasetUId == selectionContext.assetDatasetUId);
      const relatedData = assetRecipient?.relatedInfoItems?.find(r => r.datasetUId == selectionContext?.targetDatasetUId);
      return relatedData.selectInfo;
    } else {
      const assetRecipient = this.assetRecipientCountsByLayer.find(a => a.layer?.datasetUId == selectionContext?.layer.datasetUId);
      return assetRecipient.selectInfo;
    }
  };

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export interface ClosestAssetCountByLayerEx extends ClosestAssetCountByLayer {
  include: boolean;
  selectInfo: DocumentSelectionInfo;
  contactCount?: number;
  contactCountDisplay?: string;
  limitedCounts?: boolean;
  relatedInfoItems: ClosestAssetRelatedInfo[];
}

export interface ClosestAssetRelatedInfo {
  include: boolean;
  fieldName: string;
  datasetUId: string;
  datasetName: string;
  // icon info
  iconPath?: string;
  isCustomIcon?: boolean;
  color?: string;
  //
  hasDocumentFilter: boolean;
  count?: number;
  selectInfo: DocumentSelectionInfo;
  countDisplay?: string;
  contactCount?: number;
  limitedCounts?: boolean;
}

export interface ClosestAssetRelatedRequestInfo {
  parentSelectInfo: DocumentSelectionInfo;
  relatedInfo: ClosestAssetRelatedInfo;
}
